/**
 * Campaign Templates — listing + create UI (feature #60 dash surface).
 *
 * Rendered inside the `/campaigns/templates` route. Responsibilities:
 *   - Fetch merged tenant+platform catalog via TanStack Query.
 *   - Render a responsive grid of cards. Each card shows name, vertical badge,
 *     scope (platform vs tenant), description, and a "Use this" CTA that
 *     navigates to the wizard with `?template=<template_code>`.
 *   - Vertical filter and "show inactive" toggle (inactive is a no-op for
 *     regular tenants — the API only honours it for platform admins).
 *   - Empty state with a CTA to open the create modal.
 *   - Create-template modal with inline validation (no RHF/Zod present in
 *     scaffold deps; dash package.json is outside agent scope).
 *
 * No emojis, no competitor names. Light theme only. Brand gradient on the
 * headline. Zinc borders. Radius 6–8px per dash style brief.
 */

import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { toast } from "sonner";

import { ApiError } from "../../api-client";
import {
	CAMPAIGN_VERTICALS,
	type CampaignTemplateListItem,
	type CampaignVertical,
	type CreateCampaignTemplateInput,
	type ListCampaignTemplatesOptions,
	campaignTemplatesQueryKeys,
	createTenantCampaignTemplate,
	listCampaignTemplates,
} from "./api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TEMPLATE_CODE_RE = /^[a-z0-9][a-z0-9_-]{1,63}$/;

function verticalLabel(v: CampaignVertical | string | null): string {
	if (!v) return "General";
	return v.charAt(0).toUpperCase() + v.slice(1);
}

function wizardHrefFor(templateCode: string): string {
	const encoded = encodeURIComponent(templateCode);
	return `/campaigns/wizard?template=${encoded}`;
}

interface CreateFormState {
	template_code: string;
	name: string;
	description: string;
	vertical: CampaignVertical | "";
}

const EMPTY_CREATE_FORM: CreateFormState = {
	template_code: "",
	name: "",
	description: "",
	vertical: "",
};

interface CreateFormErrors {
	template_code?: string;
	name?: string;
	description?: string;
}

function validateCreateForm(state: CreateFormState): CreateFormErrors {
	const errors: CreateFormErrors = {};
	const code = state.template_code.trim();
	if (!code) errors.template_code = "Required.";
	else if (!TEMPLATE_CODE_RE.test(code))
		errors.template_code =
			"Lowercase letters, digits, hyphens, underscores. 2–64 chars.";
	const name = state.name.trim();
	if (!name) errors.name = "Required.";
	else if (name.length > 160) errors.name = "Keep name under 160 chars.";
	if (state.description.length > 500)
		errors.description = "Keep description under 500 chars.";
	return errors;
}

function buildCreatePayload(
	state: CreateFormState,
): CreateCampaignTemplateInput {
	return {
		template_code: state.template_code.trim(),
		name: state.name.trim(),
		description:
			state.description.trim().length > 0 ? state.description.trim() : null,
		vertical: state.vertical === "" ? null : state.vertical,
		payload: {},
	};
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

export function TemplatesList() {
	const [vertical, setVertical] = useState<CampaignVertical | "">("");
	const [showInactive, setShowInactive] = useState(false);
	const [isCreateOpen, setIsCreateOpen] = useState(false);

	const queryOptions: ListCampaignTemplatesOptions = useMemo(
		() => ({
			vertical: vertical === "" ? undefined : vertical,
			includeInactive: showInactive,
		}),
		[vertical, showInactive],
	);

	const queryClient = useQueryClient();

	const templatesQuery = useQuery({
		queryKey: campaignTemplatesQueryKeys.list(queryOptions),
		queryFn: () => listCampaignTemplates(queryOptions),
	});

	const createMutation = useMutation({
		mutationFn: (input: CreateCampaignTemplateInput) =>
			createTenantCampaignTemplate(input),
		onSuccess: (row: CampaignTemplateListItem) => {
			toast.success(`Template "${row.name}" created`);
			queryClient.invalidateQueries({
				queryKey: campaignTemplatesQueryKeys.all,
			});
			setIsCreateOpen(false);
		},
		onError: (err: unknown) => {
			const message =
				err instanceof ApiError
					? `${err.message}`
					: err instanceof Error
						? err.message
						: "Unable to create template.";
			toast.error(message);
		},
	});

	const isLoading = templatesQuery.isLoading;
	const isError = templatesQuery.isError;
	const templates = templatesQuery.data ?? [];
	const hasResults = templates.length > 0;

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10">
			<Header
				onCreate={() => setIsCreateOpen(true)}
				isCreateDisabled={createMutation.isPending}
			/>

			<Toolbar
				vertical={vertical}
				onVerticalChange={setVertical}
				showInactive={showInactive}
				onShowInactiveChange={setShowInactive}
			/>

			{isLoading ? (
				<LoadingGrid />
			) : isError ? (
				<ErrorState
					message={
						templatesQuery.error instanceof ApiError
							? templatesQuery.error.message
							: "Unable to load templates."
					}
					onRetry={() => {
						void templatesQuery.refetch();
					}}
				/>
			) : hasResults ? (
				<TemplatesGrid templates={templates} />
			) : (
				<EmptyState onCreate={() => setIsCreateOpen(true)} />
			)}

			{isCreateOpen ? (
				<CreateTemplateModal
					isSubmitting={createMutation.isPending}
					onCancel={() => setIsCreateOpen(false)}
					onSubmit={(state) => createMutation.mutate(buildCreatePayload(state))}
				/>
			) : null}
		</main>
	);
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

function Header({
	onCreate,
	isCreateDisabled,
}: {
	onCreate: () => void;
	isCreateDisabled: boolean;
}) {
	return (
		<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
			<div className="flex flex-col gap-1">
				<h1 className="text-brand-gradient text-4xl font-semibold tracking-tight sm:text-5xl">
					Campaign templates
				</h1>
				<p className="text-sm text-zinc-500">
					Curated campaign blueprints. Start a new campaign from a template or
					author your own tenant overrides.
				</p>
			</div>
			<button
				type="button"
				onClick={onCreate}
				disabled={isCreateDisabled}
				className="inline-flex items-center justify-center rounded-[8px] bg-zinc-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-400 disabled:cursor-not-allowed disabled:opacity-60"
			>
				New template
			</button>
		</header>
	);
}

function Toolbar({
	vertical,
	onVerticalChange,
	showInactive,
	onShowInactiveChange,
}: {
	vertical: CampaignVertical | "";
	onVerticalChange: (next: CampaignVertical | "") => void;
	showInactive: boolean;
	onShowInactiveChange: (next: boolean) => void;
}) {
	return (
		<div className="flex flex-wrap items-end gap-4 rounded-[8px] border border-zinc-200 bg-zinc-50/60 p-4">
			<label className="flex flex-col gap-1 text-xs font-medium text-zinc-600">
				<span>Vertical</span>
				<select
					value={vertical}
					onChange={(e) =>
						onVerticalChange(e.target.value as CampaignVertical | "")
					}
					className="min-w-40 rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none"
				>
					<option value="">All verticals</option>
					{CAMPAIGN_VERTICALS.map((v: CampaignVertical) => (
						<option key={v} value={v}>
							{verticalLabel(v)}
						</option>
					))}
				</select>
			</label>
			<label className="flex items-center gap-2 rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-700">
				<input
					type="checkbox"
					checked={showInactive}
					onChange={(e) => onShowInactiveChange(e.target.checked)}
					className="h-4 w-4 rounded border-zinc-400 accent-zinc-900"
				/>
				Show inactive
			</label>
		</div>
	);
}

function TemplatesGrid({
	templates,
}: {
	templates: CampaignTemplateListItem[];
}) {
	return (
		<ul
			aria-label="Campaign templates"
			className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
		>
			{templates.map((tpl) => (
				<TemplateCard key={tpl.id} template={tpl} />
			))}
		</ul>
	);
}

function TemplateCard({ template }: { template: CampaignTemplateListItem }) {
	const href = wizardHrefFor(template.template_code);
	return (
		<li className="flex h-full flex-col gap-4 rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm transition hover:border-zinc-300 hover:shadow-md">
			<div className="flex items-start justify-between gap-3">
				<div className="flex min-w-0 flex-col gap-1">
					<h2
						className="truncate text-base font-semibold text-zinc-900"
						title={template.name}
					>
						{template.name}
					</h2>
					<p className="truncate font-mono text-xs text-zinc-500">
						{template.template_code}
					</p>
				</div>
				<ScopeBadge scope={template.scope} />
			</div>
			<div className="flex flex-wrap items-center gap-2">
				<VerticalBadge vertical={template.vertical} />
				{template.is_active ? null : (
					<span className="inline-flex items-center rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
						Inactive
					</span>
				)}
			</div>
			<p className="min-h-[3rem] text-sm leading-snug text-zinc-600">
				{template.description?.trim().length
					? template.description
					: "No description."}
			</p>
			<div className="mt-auto flex items-center justify-between">
				<span className="text-xs text-zinc-400">
					Updated {formatRelative(template.updated_at)}
				</span>
				<a
					href={href}
					className="inline-flex items-center justify-center rounded-[6px] bg-gradient-to-r from-[color:var(--color-brand-emerald)] to-[color:var(--color-brand-sky)] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-zinc-400"
				>
					Use this
				</a>
			</div>
		</li>
	);
}

function ScopeBadge({ scope }: { scope: "platform" | "tenant" }) {
	const isPlatform = scope === "platform";
	return (
		<span
			className={
				isPlatform
					? "inline-flex shrink-0 items-center rounded-[6px] border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-sky-700"
					: "inline-flex shrink-0 items-center rounded-[6px] border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700"
			}
		>
			{isPlatform ? "Platform" : "Tenant"}
		</span>
	);
}

function VerticalBadge({
	vertical,
}: {
	vertical: CampaignVertical | string | null;
}) {
	return (
		<span className="inline-flex items-center rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium tracking-wide text-zinc-600">
			{verticalLabel(vertical)}
		</span>
	);
}

function LoadingGrid() {
	return (
		<ul
			aria-busy="true"
			aria-label="Loading templates"
			className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
		>
			{[0, 1, 2, 3, 4, 5].map((i) => (
				<li
					key={i}
					className="h-44 animate-pulse rounded-[8px] border border-zinc-200 bg-zinc-50"
				/>
			))}
		</ul>
	);
}

function ErrorState({
	message,
	onRetry,
}: {
	message: string;
	onRetry: () => void;
}) {
	return (
		<div className="flex flex-col items-start gap-3 rounded-[8px] border border-rose-200 bg-rose-50 p-6">
			<h2 className="text-base font-semibold text-rose-900">
				Could not load templates
			</h2>
			<p className="text-sm text-rose-800">{message}</p>
			<button
				type="button"
				onClick={onRetry}
				className="inline-flex items-center justify-center rounded-[6px] border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-800 transition hover:bg-rose-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
			>
				Try again
			</button>
		</div>
	);
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
	return (
		<div className="flex flex-col items-center gap-4 rounded-[8px] border border-dashed border-zinc-300 bg-white px-6 py-16 text-center">
			<h2 className="text-lg font-semibold text-zinc-900">
				No templates yet
			</h2>
			<p className="max-w-md text-sm text-zinc-500">
				Create a campaign template to share a reusable blueprint across your
				team, or wait for platform defaults to seed.
			</p>
			<button
				type="button"
				onClick={onCreate}
				className="inline-flex items-center justify-center rounded-[8px] bg-gradient-to-r from-[color:var(--color-brand-emerald)] to-[color:var(--color-brand-sky)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-zinc-400"
			>
				Create your first template
			</button>
		</div>
	);
}

function CreateTemplateModal({
	onCancel,
	onSubmit,
	isSubmitting,
}: {
	onCancel: () => void;
	onSubmit: (state: CreateFormState) => void;
	isSubmitting: boolean;
}) {
	const [form, setForm] = useState<CreateFormState>(EMPTY_CREATE_FORM);
	const [errors, setErrors] = useState<CreateFormErrors>({});

	function handleSubmit(e: FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const nextErrors = validateCreateForm(form);
		setErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) return;
		onSubmit(form);
	}

	function update<K extends keyof CreateFormState>(
		key: K,
		value: CreateFormState[K],
	) {
		setForm((prev) => ({ ...prev, [key]: value }));
	}

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="Create campaign template"
			className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
			onClick={(e) => {
				if (e.target === e.currentTarget && !isSubmitting) onCancel();
			}}
		>
			<form
				onSubmit={handleSubmit}
				className="flex w-full max-w-lg flex-col gap-4 rounded-[8px] border border-zinc-200 bg-white p-6 shadow-xl"
			>
				<header className="flex flex-col gap-1">
					<h2 className="text-lg font-semibold text-zinc-900">
						New campaign template
					</h2>
					<p className="text-xs text-zinc-500">
						Tenant-scoped. Platform defaults are managed separately by admins.
					</p>
				</header>

				<Field
					label="Template code"
					hint="Lowercase letters, digits, hyphens, underscores. 2–64 chars."
					error={errors.template_code}
				>
					<input
						type="text"
						value={form.template_code}
						onChange={(e) =>
							update(
								"template_code",
								e.target.value.toLowerCase().replace(/\s+/g, "-"),
							)
						}
						placeholder="summer-push-2026"
						maxLength={64}
						autoComplete="off"
						required
						className="w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 font-mono text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none"
					/>
				</Field>

				<Field label="Name" error={errors.name}>
					<input
						type="text"
						value={form.name}
						onChange={(e) => update("name", e.target.value)}
						placeholder="Summer Push 2026"
						maxLength={160}
						required
						className="w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none"
					/>
				</Field>

				<Field
					label="Description"
					hint="Optional. Up to 500 characters."
					error={errors.description}
				>
					<textarea
						value={form.description}
						onChange={(e) => update("description", e.target.value)}
						rows={3}
						maxLength={500}
						className="w-full resize-none rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none"
					/>
				</Field>

				<Field label="Vertical" hint="Optional">
					<select
						value={form.vertical}
						onChange={(e) =>
							update("vertical", e.target.value as CampaignVertical | "")
						}
						className="w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-zinc-500 focus:outline-none"
					>
						<option value="">None</option>
						{CAMPAIGN_VERTICALS.map((v) => (
							<option key={v} value={v}>
								{verticalLabel(v)}
							</option>
						))}
					</select>
				</Field>

				<footer className="mt-2 flex items-center justify-end gap-2">
					<button
						type="button"
						onClick={onCancel}
						disabled={isSubmitting}
						className="inline-flex items-center justify-center rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-300 disabled:opacity-60"
					>
						Cancel
					</button>
					<button
						type="submit"
						disabled={isSubmitting}
						className="inline-flex items-center justify-center rounded-[6px] bg-zinc-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-zinc-800 focus:outline-none focus:ring-2 focus:ring-zinc-400 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{isSubmitting ? "Creating..." : "Create template"}
					</button>
				</footer>
			</form>
		</div>
	);
}

function Field({
	label,
	hint,
	error,
	children,
}: {
	label: string;
	hint?: string;
	error?: string;
	children: React.ReactNode;
}) {
	return (
		<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
			<span>{label}</span>
			{children}
			{hint && !error ? (
				<span className="text-[11px] font-normal text-zinc-500">{hint}</span>
			) : null}
			{error ? (
				<span className="text-[11px] font-semibold text-rose-600">{error}</span>
			) : null}
		</label>
	);
}

// ---------------------------------------------------------------------------
// Utils
// ---------------------------------------------------------------------------

function formatRelative(timestamp: number): string {
	const now = Date.now();
	const diff = now - timestamp;
	if (!Number.isFinite(diff) || diff < 0) return "just now";
	const minute = 60_000;
	const hour = 60 * minute;
	const day = 24 * hour;
	if (diff < minute) return "just now";
	if (diff < hour) return `${Math.round(diff / minute)}m ago`;
	if (diff < day) return `${Math.round(diff / hour)}h ago`;
	if (diff < 30 * day) return `${Math.round(diff / day)}d ago`;
	try {
		return new Date(timestamp).toLocaleDateString();
	} catch {
		return "recently";
	}
}
